<template>
  <teleport to="body">
    <transition name="area-drawer">
      <div v-if="modelValue" class="fixed inset-0 z-[1]">
        <div class="absolute inset-0 bg-black/40" @click="close"></div>
        <div class="area-drawer-panel absolute right-0 top-0 h-full w-full max-w-md bg-white shadow-2xl flex flex-col">
          <div class="flex items-center justify-between px-6 py-4 border-b border-gray-100">
            <h3 class="text-lg font-medium text-primary-3">พื้นที่</h3>
            <button type="button" class="text-gray-400 hover:text-gray-600" @click="close">
              <XIcon class="w-5 h-5" />
            </button>
          </div>

          <div class="flex-1 overflow-y-auto px-6 py-5">
            <DashboardAreaSection
              :region="filterState.address.region"
              :province="filterState.address.province"
              :amphur="filterState.address.amphur"
              :tumbol="filterState.address.tumbol"
              :village="filterState.address.village"
              @update:region="updateAddress($event, 'region')"
              @update:province="updateAddress($event, 'province')"
              @update:amphur="updateAddress($event, 'amphur')"
              @update:tumbol="updateAddress($event, 'tumbol')"
              @update:village="updateAddress($event, 'village')"
            />
          </div>

          <div class="px-6 py-4 border-t border-gray-100 flex justify-end">
            <button
              type="button"
              class="inline-flex items-center gap-1.5 rounded-md bg-primary-1 text-white px-6 py-2.5 text-sm hover:bg-primary-3"
              @click="apply"
            >
              ดูข้อมูล
            </button>
          </div>
        </div>
      </div>
    </transition>
  </teleport>
</template>

<script setup>
import { reactive, computed, ref, watch, onUnmounted } from "vue";
import { useStore } from "vuex";
import DashboardAreaSection from "./DashboardAreaSection.vue";

const props = defineProps({
  modelValue: { type: Boolean, default: false },
});
const emit = defineEmits(["update:modelValue", "apply"]);

const store = useStore();

const INIT_FILTER_VALUE = {
  address: { region: null, province: null, amphur: null, tumbol: null, village: null },
};

const filterState = reactive(
  JSON.parse(JSON.stringify(store.getters["dashboardOverviewFilter/getAllFilter"] || INIT_FILTER_VALUE))
);

watch(() => props.modelValue, (isOpen) => {
  if (!isOpen) return;
  Object.assign(filterState, JSON.parse(JSON.stringify(
    store.getters["dashboardOverviewFilter/getAllFilter"] || INIT_FILTER_VALUE
  )));
  isInitialFill.value = !store.getters["dashboardOverviewFilter/region"];
});

const DOWNSTREAM = {
  region: ['province', 'amphur', 'tumbol', 'village'],
  province: ['amphur', 'tumbol', 'village'],
  amphur: ['tumbol', 'village'],
  tumbol: ['village'],
};

const getAddrId = (val, key) => {
  if (!val) return null;
  if (key === 'region') return val;
  if (key === 'province') return val._id?.province_ID;
  if (key === 'amphur') return val._id?.amphur_ID;
  if (key === 'tumbol') return val._id?.tumbol_ID;
  if (key === 'village') return val._id?.village_ID;
  return null;
};

const isInitialFill = ref(!store.getters["dashboardOverviewFilter/region"]);
let autoApplyTimer = null;

const updateAddress = (evt, key) => {
  const prevId = getAddrId(filterState.address[key], key);
  const newId = getAddrId(evt, key);
  filterState.address[key] = evt;
  if (prevId !== newId) {
    (DOWNSTREAM[key] || []).forEach((k) => { filterState.address[k] = null; });
  }

  if (isInitialFill.value && evt) {
    clearTimeout(autoApplyTimer);
    autoApplyTimer = setTimeout(() => {
      if (!isInitialFill.value) return;
      isInitialFill.value = false;
      store.dispatch("dashboardOverviewFilter/setStore", JSON.parse(JSON.stringify(filterState)));
      emit("apply", { ...filterState.address, label: label.value, level: level.value.key, level_name: level.value.name });
    }, 600);
  }
};

const level = computed(() => {
  const a = filterState.address;
  if (a.village) return { key: "village", name: "รายหมู่บ้าน" };
  if (a.tumbol)  return { key: "tumbol",  name: "รายหมู่บ้าน" };
  if (a.amphur)  return { key: "amphur",  name: "รายตำบล" };
  if (a.province)return { key: "province",name: "รายอำเภอ" };
  if (a.region)  return { key: "sector",  name: "รายจังหวัด" };
  return { key: "country", name: "ทั้งประเทศ" };
});

const label = computed(() => {
  const a = filterState.address;
  if (a.village) return `หมู่บ้าน${a.village.village_name}`;
  if (a.tumbol)  return `ตำบล${a.tumbol.tumbol_name}`;
  if (a.amphur)  return `อำเภอ${a.amphur.amphur_name}`;
  if (a.province)return `จังหวัด${a.province.province_name}`;
  if (a.region)  return a.region;
  return "ทั้งประเทศ";
});

const close = () => emit("update:modelValue", false);

const apply = () => {
  isInitialFill.value = false;
  clearTimeout(autoApplyTimer);
  store.dispatch("dashboardOverviewFilter/setStore", JSON.parse(JSON.stringify(filterState)));
  store.dispatch("dashboardOverviewFilter/setFilterChange", true);
  emit("apply", { ...filterState.address, label: label.value, level: level.value.key, level_name: level.value.name });
  close();
};

onUnmounted(() => clearTimeout(autoApplyTimer));
</script>

<style scoped>
.area-drawer-enter-active,
.area-drawer-leave-active {
  transition: opacity 0.2s ease;
}
.area-drawer-enter-from,
.area-drawer-leave-to {
  opacity: 0;
}
.area-drawer-enter-active .area-drawer-panel,
.area-drawer-leave-active .area-drawer-panel {
  transition: transform 0.25s ease;
}
.area-drawer-enter-from .area-drawer-panel,
.area-drawer-leave-to .area-drawer-panel {
  transform: translateX(100%);
}
</style>
